import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, RefreshCw, Wrench, MessageSquare } from "lucide-react";

export default function AdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system-health");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load system health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div data-testid="admin-health">
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-[var(--ink-2)]">
          {data?.checked_at ? `Checked: ${new Date(data.checked_at).toLocaleString()}` : ""}
        </div>
        <button type="button" className="btn btn-ghost" onClick={refresh} data-testid="admin-health-refresh">
          <RefreshCw size={14} /> Re-run checks
        </button>
      </div>

      {loading || !data ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin inline" /> Running checks…
        </div>
      ) : (
        <div className="card divide-y">
          {data.checks.map((c) => (
            <div
              key={c.service}
              className="flex items-center justify-between gap-4 p-4"
              data-testid={`health-row-${c.service.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {c.ok ? (
                  <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
                ) : (
                  <XCircle size={20} className="text-rose-600 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-semibold">{c.service}</div>
                  <div className="text-xs text-[var(--ink-2)] truncate">{c.detail}</div>
                </div>
              </div>
              <span
                className={
                  "text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded " +
                  (c.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700")
                }
              >
                {c.ok ? "OK" : "DOWN"}
              </span>
            </div>
          ))}
        </div>
      )}

      <MaintenanceSection />
    </div>
  );
}


function MaintenanceSection() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        "Soft-delete duplicate forum posts? This is reversible (posts are flagged, not destroyed)."
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/forum/dedupe${dryRun ? "?dry_run=true" : ""}`,
      );
      setReport(data);
      if (dryRun) {
        toast.success(`Preview: would remove ${data.duplicates_removed} dups + ${data.stale_seeds_removed} stale seeds`);
      } else {
        toast.success(`Removed ${data.duplicates_removed} duplicates + ${data.stale_seeds_removed} stale seeds`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dedupe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6" data-testid="admin-maintenance">
      <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] mb-2 flex items-center gap-1">
        <Wrench size={11} /> Maintenance
      </div>
      <div className="card p-4 space-y-3">
        <div className="flex items-start gap-3 flex-wrap">
          <MessageSquare size={20} className="text-[var(--ink-2)] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Forum: Dedupe Posts</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5 leading-snug">
              Soft-deletes duplicate posts (grouped by title) and stale seed posts
              whose title is no longer in the current seed list. Replies on
              removed posts cascade.
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="btn btn-ghost text-xs"
              onClick={() => run(true)}
              disabled={busy}
              data-testid="admin-forum-dedupe-dry"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : null}
              Preview
            </button>
            <button
              type="button"
              className="btn btn-primary text-xs"
              onClick={() => run(false)}
              disabled={busy}
              data-testid="admin-forum-dedupe-run"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : null}
              Run cleanup
            </button>
          </div>
        </div>

        {report && (
          <div className="mt-2 p-3 bg-stone-50 border border-[var(--line)] rounded-md text-xs space-y-2"
               data-testid="admin-forum-dedupe-report">
            <div className="flex gap-4 flex-wrap">
              <span><strong>Duplicates removed:</strong> {report.duplicates_removed}</span>
              <span><strong>Stale seeds removed:</strong> {report.stale_seeds_removed}</span>
              {report.dry_run && (
                <span className="text-amber-700 font-bold">[DRY RUN]</span>
              )}
            </div>
            {report.groups?.length > 0 && (
              <details>
                <summary className="cursor-pointer font-semibold text-[var(--ink-2)]">
                  Groups affected ({report.groups.length})
                </summary>
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                  {report.groups.map((g, i) => (
                    <div key={i} className="flex gap-2 text-[var(--ink-2)]">
                      <span className="font-mono text-[10px] bg-stone-200 px-1.5 py-0.5 rounded shrink-0">
                        {g.removed_count}×
                      </span>
                      <span className="truncate">{g.title}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
